import {
  BarChart3, ShieldAlert, Factory, CreditCard,
} from 'lucide-react';
import { hardwareApi, money, type DeviceModelRow, type DeviceUnitRow } from '../../../services/hardwareApi';
import { billingApi, type FinanceAgreementRow, type AgreementRollupLike } from '../../../services/billingApi';
import { inventoryApi } from '../../../services/inventoryApi';

/**
 * INVENTORY REPORTS REGISTRY.
 *
 * Adding a future report requires ONLY a registry entry + an `endpoint`
 * function that resolves to the uniform `ReportResult` shape below — nothing
 * in `ReportsPage.tsx` changes. `endpoint` IS the adapter: it may call any
 * backend (hardware-service, billing-service, a future one) and is
 * responsible for shaping whatever that backend returns into
 * `{ columns, rows, totals }` — the one contract the shell renders,
 * paginates none of, and exports to CSV generically.
 *
 * `endpoint` may throw (a 404 before its backend has shipped, a real
 * failure); ReportsPage catches per-report and shows that report's own
 * error/empty state rather than taking the whole page down.
 */

export interface ReportColumn {
  key: string;
  label: string;
  format?: 'money' | 'date' | 'percent' | 'number' | 'text';
}

export interface ReportResult {
  columns: ReportColumn[];
  rows: Record<string, any>[];
  totals?: Record<string, any>;
}

export interface ReportParamOption {
  value: string;
  label: string;
}

export interface ReportParam {
  key: string;
  label: string;
  type: 'select' | 'date' | 'text';
  options?: ReportParamOption[];
}

export interface ReportDef {
  id: string;
  title: string;
  description: string;
  icon: React.FC<any>;
  endpoint: (params: Record<string, string>) => Promise<ReportResult>;
  params?: ReportParam[];
  render: 'table' | 'cards';
}

const DEPLOYED_STATES = new Set(['shipped', 'delivered', 'deployed']);
const HOLD_STATES = new Set(['on_hold', 'backordered', 'rma', 'returned', 'retired']);
// inventory-hub-unit-lifecycle wave — the two new per-model provisioning
// stops (B1), bucketed on their own so a unit mid-checklist is counted
// somewhere rather than silently falling out of every column below.
const PROVISIONING_STATES = new Set(['provisioning', 'configured']);
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

/**
 * Stock & valuation, computed CLIENT-SIDE from data that already exists
 * today (`hardwareApi.listModels` + `listUnits`) — no dependency on the
 * `/reports/stock` endpoint wave W2 may or may not land tonight. When that
 * endpoint exists and does server-side aging/valuation better (e.g. against
 * actual per-GRN cost rather than the model's current landed cost), swap the
 * body here for a call to `inventoryApi.reportStock()` — the registry entry
 * and the shell need not change.
 */
async function stockReport(): Promise<ReportResult> {
  const [m, u] = await Promise.all([hardwareApi.listModels(), hardwareApi.listUnits()]);
  const models: DeviceModelRow[] = m.items || [];
  const units: DeviceUnitRow[] = u.items || [];
  const now = Date.now();
  const byModel = new Map<string, DeviceUnitRow[]>();
  for (const unit of units) {
    if (!byModel.has(unit.modelId)) byModel.set(unit.modelId, []);
    byModel.get(unit.modelId)!.push(unit);
  }

  const rows = models.map((model) => {
    const modelUnits = byModel.get(model.modelId) || [];
    const inStock = modelUnits.filter((x) => x.state === 'in_stock');
    const allocated = modelUnits.filter((x) => x.state === 'allocated').length;
    const provisioning = modelUnits.filter((x) => PROVISIONING_STATES.has(x.state)).length;
    const deployed = modelUnits.filter((x) => DEPLOYED_STATES.has(x.state)).length;
    const rma = modelUnits.filter((x) => HOLD_STATES.has(x.state)).length;
    const agingOver60 = inStock.filter((x) => now - x.createdAt > SIXTY_DAYS_MS).length;
    const valueCents = inStock.length * (model.landedCostCents || 0);
    return {
      model: model.modelName,
      inStock: inStock.length,
      allocated,
      provisioning,
      deployed,
      rma,
      costCents: model.landedCostCents || 0,
      valueCents,
      agingOver60,
    };
  });

  const totals = {
    valueCents: rows.reduce((s, r) => s + r.valueCents, 0),
    inStock: rows.reduce((s, r) => s + r.inStock, 0),
    agingOver60: rows.reduce((s, r) => s + r.agingOver60, 0),
  };

  return {
    columns: [
      { key: 'model', label: 'Model' },
      { key: 'inStock', label: 'In stock', format: 'number' },
      { key: 'allocated', label: 'Allocated', format: 'number' },
      { key: 'provisioning', label: 'Provisioning', format: 'number' },
      { key: 'deployed', label: 'Deployed', format: 'number' },
      { key: 'rma', label: 'RMA', format: 'number' },
      { key: 'costCents', label: 'Unit cost', format: 'money' },
      { key: 'valueCents', label: 'Value at cost', format: 'money' },
      { key: 'agingOver60', label: '> 60d in stock', format: 'number' },
    ],
    rows,
    totals,
  };
}

/**
 * Damage & write-offs — `GET /reports/damage` (hardware-service) already
 * returns the registry's uniform `{ columns, rows, totals }` shape (grouped
 * stage × chargedTo, server-side); passed straight through.
 */
async function damageReport(): Promise<ReportResult> {
  return inventoryApi.report('damage');
}

/**
 * Vendor performance — `GET /reports/vendors` returns the uniform shape
 * (per-vendor scorecard: on-time %, defect %, spend); passed straight through.
 */
async function vendorReport(): Promise<ReportResult> {
  return inventoryApi.report('vendors');
}

/**
 * Financing book. `GET /financing` on billing-service is REAL and already
 * live (`financing.ts`'s `listFinancing`, derived live from Stripe) — this is
 * the one report in this seed set that is not waiting on anything tonight.
 * Adapted here, in the client, from its native `{ agreements }` shape into
 * the registry's uniform `{ columns, rows, totals }`.
 */
async function financingBookReport(): Promise<ReportResult> {
  const all: FinanceAgreementRow[] = [];
  let totals: AgreementRollupLike | undefined;
  let nextToken: string | undefined;
  // Hard stop at 200 pages (20,000 rows) so a server-side paging bug can never
  // spin this forever. Reaching it is a bug, and it says so.
  for (let page = 0; page < 200; page += 1) {
    const res = await billingApi.listFinancing({ status: 'all', limit: 100, nextToken });
    all.push(...res.agreements);
    totals = res.totals ?? totals;
    nextToken = res.nextToken ?? undefined;
    if (!nextToken) break;
    if (page === 199) console.error('[financingBookReport] page cap hit — report may be incomplete');
  }

  const rows = all.map((a) => ({
    tenant: a.tenantName || a.tenantId || '—',
    sku: a.name,
    type: a.financeType,
    monthlyCents: a.monthlyCents,
    totalCents: a.totalCents ?? 0,
    paidCents: a.paidCents,
    remainingCents: a.remainingCents ?? 0,
    status: a.subscriptionStatus,
  }));

  return {
    columns: [
      { key: 'tenant', label: 'Tenant' },
      { key: 'sku', label: 'Offer' },
      { key: 'type', label: 'Type' },
      { key: 'monthlyCents', label: 'Monthly', format: 'money' },
      { key: 'totalCents', label: 'Total', format: 'money' },
      { key: 'paidCents', label: 'Paid', format: 'money' },
      { key: 'remainingCents', label: 'Outstanding', format: 'money' },
      { key: 'status', label: 'Subscription status' },
    ],
    rows,
    totals: {
      monthlyCents: totals?.totalMonthlyCents ?? rows.reduce((s, r) => s + r.monthlyCents, 0),
      remainingCents: totals?.totalOutstandingCents ?? rows.reduce((s, r) => s + r.remainingCents, 0),
    },
  };
}

export const REPORTS: ReportDef[] = [
  {
    id: 'stock-valuation',
    title: 'Stock & valuation',
    description: 'On-hand by model × state, at cost, aging (units in stock > 60d flagged).',
    icon: BarChart3,
    endpoint: stockReport,
    render: 'table',
  },
  {
    id: 'financing-book',
    title: 'Financing book',
    description: 'Outstanding by tenant, overdue, projected monthly hardware revenue.',
    icon: CreditCard,
    endpoint: financingBookReport,
    render: 'table',
  },
  {
    id: 'vendor-performance',
    title: 'Vendor performance',
    description: 'On-time %, defect %, landed cost trend per vendor.',
    icon: Factory,
    endpoint: vendorReport,
    render: 'table',
  },
  {
    id: 'damage-loss',
    title: 'Damage & loss',
    description: 'By stage (receiving/stock/deployed), who paid, write-off total.',
    icon: ShieldAlert,
    endpoint: damageReport,
    render: 'table',
  },
];

/** cents -> "$X.XX", re-exported so the shell needs no second money formatter. */
export { money };
